export type WatchEvent = {
  outcome: "slow" | "timeout" | "late_resolved" | "late_rejected"
  elapsedMs: number
  error?: unknown
}

type WatchOptions = {
  slowMs?: number
  timeoutMs?: number
  onEvent?: (event: WatchEvent) => void
}

export class WatchPromiseTimeoutError extends Error {
  elapsedMs: number

  constructor(elapsedMs: number) {
    super(`Promise timed out after ${elapsedMs}ms`)
    this.name = "WatchPromiseTimeoutError"
    this.elapsedMs = elapsedMs
  }
}

export const watchPromise = <T>(promise: Promise<T>, options: WatchOptions = {}) =>
  new Promise<T>((resolve, reject) => {
    const startedAt = Date.now()
    const elapsedMs = () => Date.now() - startedAt
    const slowTimer = setTimeout(
      () => options.onEvent?.({ outcome: "slow", elapsedMs: elapsedMs() }),
      options.slowMs ?? 1000,
    )
    const timeoutTimer = setTimeout(() => {
      settled = true
      clearTimeout(slowTimer)
      options.onEvent?.({ outcome: "timeout", elapsedMs: elapsedMs() })
      reject(new WatchPromiseTimeoutError(elapsedMs()))
    }, options.timeoutMs ?? 5000)
    let settled = false

    const settle = (callback: (value: T) => void, value: T) => {
      settled = true
      clearTimeout(slowTimer)
      clearTimeout(timeoutTimer)
      callback(value)
    }

    promise.then(
      (value) => {
        if (settled) options.onEvent?.({ outcome: "late_resolved", elapsedMs: elapsedMs() })
        else settle(resolve, value)
      },
      (error) => {
        if (settled) options.onEvent?.({ outcome: "late_rejected", elapsedMs: elapsedMs(), error })
        else settle(reject, error)
      },
    )
  })
